import { TimeInput } from '@/components/ui/Input'
import { Chip } from '@/components/ui/Chip'
import { Segmented } from '@/components/ui/Segmented'
import { SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValue } from '@/components/ui/Select'
import { Toggle } from '@/components/ui/Toggle'
import { cn } from '@/lib/cn'
import { videoFormatLabel } from '@/lib/videoFormats'
import { PriorityFormatList } from '../PriorityFormatList'
import { CONFLICT_OPTIONS, MAX_SLOTS, SELECTION_MODES, type ScheduleValues } from './model'
import { Disclosure, Field, Help, NumberInput, Section, TimeChipInput } from './parts'
import styles from './ChannelEditDialog.module.scss'

export interface UploadTabProps {
  values: ScheduleValues
  errors: Record<string, string>
  set: <K extends keyof ScheduleValues>(key: K, value: ScheduleValues[K]) => void
  before: (key: keyof ScheduleValues) => string | undefined
  slots: string[]
  slotsBefore: string | undefined
  onSlotsChange: (slots: string[]) => void
  /** Factory dos formatos da ordem de upload (a do pool, no canal ligado a um). */
  formatFactory?: string
}

const DEFAULT_BACKLOG_DAYS = 7

export function UploadTab({
  values,
  errors,
  set,
  before,
  slots,
  slotsBefore,
  onSlotsChange,
  formatFactory,
}: UploadTabProps) {
  const mode = SELECTION_MODES.find((m) => m.value === values.upload_selection_mode)
  const conflict = CONFLICT_OPTIONS.find((c) => c.value === values.on_slot_conflict)
  const inherited = values.uploads_per_day === null
  const advancedCustom =
    values.backlog_max_age_days !== DEFAULT_BACKLOG_DAYS || values.max_uploads_per_cycle !== null
  const advancedError = Boolean(errors.backlog_max_age_days || errors.max_uploads_per_cycle)

  return (
    <div className={styles.tabPanel}>
      <Section title="Escolha do vídeo">
        <Field
          id="ch-selection-mode"
          label="Critério"
          help="Como o ciclo de upload escolhe o vídeo do dia no backlog."
          before={before('upload_selection_mode')}
          hint={mode?.hint}
        >
          <SelectRoot
            value={values.upload_selection_mode}
            onValueChange={(v) => {
              set('upload_selection_mode', v as ScheduleValues['upload_selection_mode'])
            }}
          >
            <SelectTrigger id="ch-selection-mode" aria-label="Critério de escolha do vídeo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SELECTION_MODES.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
        </Field>
        {values.upload_selection_mode === 'priority' ? (
          <Field
            label="Ordem dos formatos"
            meta="arraste ou use as setas"
            before={before('upload_priority_types') ?? before('disabled_video_types')}
            hint={
              values.upload_priority_types.length > 0
                ? 'Formato desativado deixa de ser gerado e de entrar na fila de upload; o que já foi agendado no YouTube segue. Ao salvar, ele vai para o fim da lista.'
                : undefined
            }
          >
            <PriorityFormatList
              factory={formatFactory ?? values.factory}
              value={values.upload_priority_types}
              onChange={(next) => {
                set('upload_priority_types', next)
              }}
              disabled={values.disabled_video_types}
              onDisabledChange={(next) => {
                set('disabled_video_types', next)
              }}
            />
          </Field>
        ) : values.disabled_video_types.length > 0 ? (
          // O desligado vale em qualquer critério; fora do "Prioridade por formato"
          // a lista some, e sem esta linha o estado ficaria escondido.
          <p className={styles.hint}>
            Desativados (fora da geração e da fila de upload):{' '}
            {values.disabled_video_types.map((slug) => videoFormatLabel(values.factory, slug)).join(', ')}. Para
            religar, escolha "Prioridade por formato".
          </p>
        ) : null}
        <p className={styles.hint}>Vídeos bloqueados pelo filtro de segurança nunca sobem.</p>
      </Section>

      <Section title="Quando agendar">
        <div className={styles.grid2}>
          <Field
            id="ch-upl-time"
            label="Horário do ciclo de upload"
            before={before('upload_time')}
            error={errors.upload_time}
            hint="Hora em que o robô escolhe e agenda os vídeos — não a hora em que ficam públicos."
          >
            <TimeInput
              id="ch-upl-time"
              value={values.upload_time}
              onChange={(v) => {
                set('upload_time', v)
              }}
              error={Boolean(errors.upload_time)}
            />
          </Field>
          <Field
            id="ch-upd"
            label="Vídeos por dia"
            meta={inherited ? 'padrão: igual à geração' : 'personalizado'}
            before={before('uploads_per_day')}
            error={errors.uploads_per_day}
            className={cn(inherited && styles.inherited)}
          >
            <NumberInput
              id="ch-upd"
              min={0}
              max={10}
              suffix="0–10"
              value={inherited ? values.videos_per_day : values.uploads_per_day}
              error={Boolean(errors.uploads_per_day)}
              onChange={(v) => {
                set('uploads_per_day', v ?? Number.NaN)
              }}
            />
            {!inherited ? (
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => {
                  set('uploads_per_day', null)
                }}
              >
                Voltar ao padrão ({values.videos_per_day}, igual à geração)
              </button>
            ) : null}
          </Field>
        </div>
        <Field
          label="Se o dia já tem uploads programados"
          help="O que fazer quando o ciclo encontra o dia já cheio de uploads programados."
          before={before('on_slot_conflict')}
          hint={conflict?.hint}
        >
          <Segmented
            aria-label="Se o dia já tem uploads programados"
            options={CONFLICT_OPTIONS.map((c) => ({ value: c.value, label: c.label }))}
            value={values.on_slot_conflict}
            onValueChange={(v) => {
              set('on_slot_conflict', v)
            }}
          />
        </Field>
      </Section>

      <Section title="Quando publicar">
        <div className={styles.toggleHead}>
          <Toggle
            id="ch-use-peak"
            aria-label="Upload dinâmico pelo horário de pico"
            checked={values.use_peak_hour}
            onCheckedChange={(v) => {
              set('use_peak_hour', v)
            }}
          />
          <div className={styles.toggleHeadText}>
            <span className={styles.labelRow}>
              <span>Upload dinâmico pelo horário de pico</span>
              <Help label="horário de pico">
                O ciclo calcula o horário de maior audiência do canal e espaça os vídeos do dia dentro da janela.
              </Help>
            </span>
            <span className={styles.hint}>Cada vídeo fica público no pico de audiência do canal, dentro da janela.</span>
          </div>
        </div>
        {values.use_peak_hour ? (
          <div className={styles.grid2}>
            <Field
              id="ch-window-start"
              label="Início da janela"
              before={before('publish_window_start')}
              error={errors.publish_window_start}
            >
              <TimeInput
                id="ch-window-start"
                value={values.publish_window_start}
                onChange={(v) => {
                  set('publish_window_start', v)
                }}
                error={Boolean(errors.publish_window_start)}
              />
            </Field>
            <Field
              id="ch-window-end"
              label="Fim da janela"
              before={before('publish_window_end')}
              error={errors.publish_window_end}
            >
              <TimeInput
                id="ch-window-end"
                value={values.publish_window_end}
                onChange={(v) => {
                  set('publish_window_end', v)
                }}
                error={Boolean(errors.publish_window_end)}
              />
            </Field>
          </div>
        ) : null}
        <Field
          id="ch-new-slot"
          label="Slots de publicação"
          meta={
            <>
              {values.use_peak_hour ? <Chip>Fallback</Chip> : null}
              <span>
                {slots.length}/{MAX_SLOTS}
              </span>
            </>
          }
          before={slotsBefore}
          hint={
            values.use_peak_hour
              ? 'Com o pico ligado, os slots só valem se o canal ainda não tem dados de pico ou se o cálculo falhar.'
              : 'Cada vídeo do dia fica público num destes horários.'
          }
        >
          <TimeChipInput id="ch-new-slot" value={slots} onChange={onSlotsChange} />
        </Field>
      </Section>

      <Disclosure
        title="Avançado"
        meta={advancedCustom ? 'valores personalizados' : '2 campos · usando padrão'}
        defaultOpen={advancedError}
      >
        <div className={styles.grid2}>
          <Field
            id="ch-backlog-age"
            label="Idade máxima do backlog"
            help="Vídeos mais velhos que isso deixam de subir e viram candidatos à limpeza."
            meta={values.backlog_max_age_days === DEFAULT_BACKLOG_DAYS ? 'padrão' : undefined}
            before={before('backlog_max_age_days')}
            error={errors.backlog_max_age_days}
          >
            <NumberInput
              id="ch-backlog-age"
              min={1}
              max={30}
              suffix="dias · 1–30"
              value={values.backlog_max_age_days}
              error={Boolean(errors.backlog_max_age_days)}
              onChange={(v) => {
                set('backlog_max_age_days', v ?? Number.NaN)
              }}
            />
          </Field>
          <Field
            id="ch-max-uploads"
            label="Máx. de uploads por ciclo"
            help="Teto de uploads por ciclo. Vazio usa os slots e mais 1 exploratório por dia; 1 corta o exploratório."
            meta={values.max_uploads_per_cycle === null ? 'padrão' : undefined}
            before={before('max_uploads_per_cycle')}
            error={errors.max_uploads_per_cycle}
          >
            <NumberInput
              id="ch-max-uploads"
              min={1}
              max={10}
              allowEmpty
              placeholder="padrão do sistema"
              value={values.max_uploads_per_cycle}
              error={Boolean(errors.max_uploads_per_cycle)}
              onChange={(v) => {
                set('max_uploads_per_cycle', v)
              }}
            />
          </Field>
        </div>
      </Disclosure>
    </div>
  )
}
